import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fetchSession } from '../utils/authClient';

const DashboardContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const DashboardHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  color: #cccccc;
  font-size: 1.1rem;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const DashboardCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 15px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: #00ff88;
    box-shadow: 0 10px 30px rgba(0, 255, 136, 0.2);
  }
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #00ff88;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardIcon = styled.span`
  font-size: 1.8rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #cccccc;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #ffffff;
  font-weight: 600;
`;

const SkylinkNumber = styled.div`
  background: linear-gradient(45deg, #00ff88, #00d4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
  padding: 1rem;
  border: 2px solid rgba(0, 255, 136, 0.3);
  border-radius: 10px;
  margin: 1rem 0;
`;

const ActionButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000000;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 255, 136, 0.4);
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const QuickActionButton = styled(Link)`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  color: #00d4ff;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;

  &.active {
    background: rgba(0, 255, 136, 0.2);
    color: #00ff88;
    border: 1px solid #00ff88;
  }

  &.pending {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
    border: 1px solid #ffc107;
  }

  &.inactive {
    background: rgba(255, 68, 68, 0.2);
    color: #ff4444;
    border: 1px solid #ff4444;
  }
`;

const BalanceDisplay = styled.div`
  text-align: center;
  margin: 1rem 0;
`;

const BalanceAmount = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #00ff88;
  margin-bottom: 0.5rem;
`;

const BalanceLabel = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #888888;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [inmateAccounts, setInmateAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const session = await fetchSession();
      if (session.network || session.error) {
        setError(session.message || 'Unable to load account.');
        return;
      }
      if (session.status === 401) {
        navigate('/signin', { state: { from: '/dashboard' } });
        return;
      }
      if (session.status === 403) {
        setDenied(session.message || 'This account cannot use the dashboard yet.');
        return;
      }

      const dashRes = await fetch('/api/dashboard', { credentials: 'include' });
      if (dashRes.status === 401) {
        navigate('/signin', { state: { from: '/dashboard' } });
        return;
      }
      if (dashRes.status === 403) {
        const dashData = await dashRes.json().catch(() => ({}));
        setDenied(dashData.message || 'This account cannot use the dashboard yet.');
        return;
      }
      if (!dashRes.ok) {
        setError('Unable to load dashboard.');
        return;
      }
      const dashData = await dashRes.json();
      setUserData(dashData.user || session.user);
      setInmateAccounts(Array.isArray(dashData.inmates) ? dashData.inmates : []);
    } catch (err) {
      setError('Unable to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: '#00ff88', fontSize: '2rem' }}>Loading...</div>
        </div>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#ff8888' }}>
          {error}
        </div>
      </DashboardContainer>
    );
  }

  if (denied) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#cccccc' }}>
          {denied}
        </div>
      </DashboardContainer>
    );
  }

  if (!userData) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: '#00ff88', fontSize: '2rem' }}>Loading...</div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <Title>Welcome back, {userData?.firstName}!</Title>
        <Subtitle>Manage your Skylink accounts and stay connected</Subtitle>
      </DashboardHeader>

      <DashboardGrid>
        {/* User Account Card */}
        <DashboardCard>
          <CardTitle>
            <CardIcon>👤</CardIcon>
            Your Account
          </CardTitle>
          
          <InfoRow>
            <InfoLabel>Username:</InfoLabel>
            <InfoValue>{userData?.username}</InfoValue>
          </InfoRow>
          

          
          <InfoRow>
            <InfoLabel>Phone Number:</InfoLabel>
            <InfoValue>{userData?.phoneNumber}</InfoValue>
          </InfoRow>
          
          <InfoRow>
            <InfoLabel>Account Type:</InfoLabel>
            <InfoValue>{userData?.accountType}</InfoValue>
          </InfoRow>
          
          <InfoRow>
            <InfoLabel>Member Since:</InfoLabel>
            <InfoValue>{new Date(userData?.createdAt).toLocaleDateString()}</InfoValue>
          </InfoRow>

          <QuickActions>
            <QuickActionButton to="/profile">View Profile</QuickActionButton>
            <QuickActionButton to="/settings">Settings</QuickActionButton>
          </QuickActions>
        </DashboardCard>

        {/* Inmate Accounts Card */}
        <DashboardCard>
          <CardTitle>
            <CardIcon>📞</CardIcon>
            Inmate Accounts
          </CardTitle>
          
          {inmateAccounts.length > 0 ? (
            inmateAccounts.map(account => (
              <div key={account.id} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px' }}>
                <InfoRow>
                  <InfoLabel>Inmate:</InfoLabel>
                  <InfoValue>{account.inmateName}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>ID:</InfoLabel>
                  <InfoValue>{account.inmateId}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Facility:</InfoLabel>
                  <InfoValue>{account.facilityName}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>State:</InfoLabel>
                  <InfoValue>{account.state}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Relationship:</InfoLabel>
                  <InfoValue>{account.relationship}</InfoValue>
                </InfoRow>
                
                <InfoRow>
                  <InfoLabel>Status:</InfoLabel>
                  <StatusBadge className={account.status}>{account.status}</StatusBadge>
                </InfoRow>

                {account.skylinkNumber && (
                  <div style={{ marginTop: '1rem' }}>
                    <InfoLabel style={{ display: 'block', marginBottom: '0.5rem' }}>Skylink Number:</InfoLabel>
                    <SkylinkNumber>{account.skylinkNumber}</SkylinkNumber>
                  </div>
                )}

                <BalanceDisplay>
                  <BalanceAmount>${Number(account.balance || 0).toFixed(2)}</BalanceAmount>
                  <BalanceLabel>Current Balance</BalanceLabel>
                </BalanceDisplay>

                <QuickActions>
                  <ActionButton to={`/load-money?inmateId=${account.inmateId}`}>
                    Load Money
                  </ActionButton>
                  <QuickActionButton to={`/account/${account.id}`}>
                    View Details
                  </QuickActionButton>
                </QuickActions>
              </div>
            ))
          ) : (
            <EmptyState>
              <EmptyStateIcon>📞</EmptyStateIcon>
              <div>No inmate accounts yet</div>
              <div style={{ marginTop: '1rem' }}>
                <ActionButton to="/inmate-signup">Create Inmate Account</ActionButton>
              </div>
            </EmptyState>
          )}
        </DashboardCard>

        {/* Quick Actions Card */}
        <DashboardCard>
          <CardTitle>
            <CardIcon>⚡</CardIcon>
            Quick Actions
          </CardTitle>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <ActionButton to="/inmate-signup">
              Create Inmate Account
            </ActionButton>
            
            <ActionButton to="/connect-to-inmate">
              Connect to Inmate
            </ActionButton>
            
            <ActionButton to="/load-money">
              Load Money
            </ActionButton>
            
            <ActionButton to="/support">
              Contact Support
            </ActionButton>
            
            <ActionButton to="/faq">
              FAQ & Help
            </ActionButton>
          </div>
        </DashboardCard>

        {/* Recent Activity Card */}
        <DashboardCard>
          <CardTitle>
            <CardIcon>📊</CardIcon>
            Recent Activity
          </CardTitle>
          
          <div style={{ color: '#cccccc', textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📈</div>
            <div>Activity tracking coming soon!</div>
            <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.7 }}>
              View call history, payments, and account updates
            </div>
          </div>
        </DashboardCard>
      </DashboardGrid>
    </DashboardContainer>
  );
};

export default Dashboard; 